import React from "react";
import styled from "styled-components";
import { format as formatDate, isValid } from "date-fns";
import { formatCurrency } from "../config/market";
import { hasSalePrice } from "../utils/vendorDiscounts";

/**
 * PriceCut: the price row of a product that may be on offer.
 *
 * With no sale price it renders the price exactly the way the page already
 * does: the page's own styled price component (or className), the page's own
 * formatter, nothing else. With a sale price it renders the struck list price
 * first, then the sale price in that same page style, so a discounted card
 * looks like the card next to it with one extra grey number in front.
 *
 * `component` is the page's existing styled price element; the struck price
 * is nested inside it so the page's spacing (padding, margins, nowrap) keeps
 * applying to the whole row, while the struck span overrides only its own
 * size, weight, colour and decoration.
 *
 * `savings` (a number) adds the "You save" pill under the row, and `endsAt`
 * adds "Offer ends d MMM" beside it. Cards leave both out; dialogs pass them.
 */

export const StruckPrice = styled.span`
  display: inline;
  margin-right: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: #9a9fa5;
  text-decoration: line-through;
  text-shadow: none;
  letter-spacing: 0;
  white-space: nowrap;
`;

const Stack = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: ${(p) => (p.$align === "end" ? "flex-end" : "flex-start")};
  gap: 4px;
  min-width: 0;
`;

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
`;

export const SavePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: #e8f5ec;
  color: #1e6b3a;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
`;

const Ends = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  color: #9a9fa5;
  white-space: nowrap;
`;

/* `Number(null)` is 0 and `Number("")` is 0, and both are finite, so a plain
   `Number.isFinite(Number(v))` reads an absent value as zero. Absent means
   absent: null, undefined and "" are never numbers here. */
const hasNumber = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  typeof value !== "boolean" &&
  Number.isFinite(Number(value));

/** "12 Sep" for an ISO date string, or "" when the date cannot be read. */
export const formatOfferEnd = (endsAt) => {
  if (!endsAt) return "";
  const d = endsAt instanceof Date ? endsAt : new Date(endsAt);
  return isValid(d) ? formatDate(d, "d MMM") : "";
};

/**
 * OfferMeta: the "You save" pill and the "Offer ends d MMM" note, on their
 * own, for pages whose price already sits inside a pill of its own.
 * Renders nothing when there is nothing to say.
 */
export const OfferMeta = ({ savings, endsAt = null, format = formatCurrency }) => {
  const saved = hasNumber(savings) ? Number(savings) : 0;
  const ends = formatOfferEnd(endsAt);
  if (saved <= 0 && !ends) return null;
  return (
    <Meta>
      {saved > 0 && <SavePill>You save {format(saved)}</SavePill>}
      {ends && <Ends>Offer ends {ends}</Ends>}
    </Meta>
  );
};

const PriceCut = ({
  price,
  salePrice = null,
  listPrice,
  component: Component = "span",
  className,
  format = formatCurrency,
  prefix = "",
  savings,
  endsAt = null,
  align = "start",
  ...rest
}) => {
  const list = hasNumber(listPrice) ? Number(listPrice) : Number(price);
  /* One definition of "on offer" for the whole web client, shared with the
     cart and the product pages, so the row can never disagree with the price
     the cart charges. */
  const onSale = hasSalePrice({ salePrice, listPrice: list });

  if (!onSale) {
    return (
      <Component className={className} {...rest}>
        {prefix}
        {format(price)}
      </Component>
    );
  }

  const row = (
    <Component className={className} {...rest}>
      <StruckPrice aria-label={`Was ${format(list)}`}>{format(list)}</StruckPrice>
      {prefix}
      {format(Number(salePrice))}
    </Component>
  );

  const saved = hasNumber(savings) ? Number(savings) : 0;
  if (saved <= 0 && !formatOfferEnd(endsAt)) return row;

  return (
    <Stack $align={align}>
      {row}
      <OfferMeta savings={saved} endsAt={endsAt} format={format} />
    </Stack>
  );
};

/* The cart's "Vendor offer" line: what the live offers took off, shown
   negative and green beside the totals. Totals are already net of it. */
export const VendorOfferAmount = styled.span`
  color: #1e6b3a;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const OfferRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;

  .k {
    color: #66787b;
    font-weight: 600;
  }
`;

/**
 * VendorOfferLine: "Vendor offer   -UGX 2,000". Renders nothing unless the
 * amount is above zero, so carts can drop it in unconditionally.
 */
export const VendorOfferLine = ({
  amount,
  format = formatCurrency,
  label = "Vendor offer",
  className,
  style,
}) => {
  const n = Number(amount);
  if (!(n > 0)) return null;
  return (
    <OfferRow className={className} style={style}>
      <span className="k">{label}</span>
      <VendorOfferAmount className="v">-{format(n)}</VendorOfferAmount>
    </OfferRow>
  );
};

export default PriceCut;
